import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Manual (backdatable) follow-up task creation — supervisors/admins.
export const ManualFollowUpDialog = ({ open, onOpenChange, domainId, agents = [], onCreated }) => {
  const [agentId, setAgentId] = useState('');
  const [phone, setPhone] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const p = domainId ? `?domain_id=${domainId}` : '';
      const r = await axios.post(`${API}/follow-ups/manual${p}`, {
        agent_id: agentId, customer_phone: phone, due_date: dueDate, note,
      });
      toast.success(r.data.overdue
        ? 'Task created — already overdue, it will appear in the weekly rollup'
        : 'Follow-up task created');
      setAgentId(''); setPhone(''); setDueDate(''); setNote('');
      onOpenChange(false);
      onCreated?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not create task');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="manual-followup-dialog">
        <DialogHeader>
          <DialogTitle>Add follow-up task</DialogTitle>
          <DialogDescription>
            For wins that predate the feature or off-platform sales. The due date can be in the
            past — backdated tasks show as overdue straight away.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-slate-600">Agent</label>
            {agents.length > 0 ? (
              <select
                value={agentId} onChange={(e) => setAgentId(e.target.value)} required
                className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm bg-white"
                data-testid="manual-followup-agent-select"
              >
                <option value="">Select agent…</option>
                {agents.map(a => <option key={a.agent_id} value={a.agent_id}>{a.agent_name} (ext {a.agent_id})</option>)}
              </select>
            ) : (
              <input
                type="text" value={agentId} onChange={(e) => setAgentId(e.target.value)} required
                placeholder="Agent extension, e.g. 891"
                className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
                data-testid="manual-followup-agent-input"
              />
            )}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Customer phone</label>
            <input
              type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required
              placeholder="+61 4xx xxx xxx"
              className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
              data-testid="manual-followup-phone-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Due date (past dates allowed)</label>
            <input
              type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required
              className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
              data-testid="manual-followup-due-input"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Note (optional)</label>
            <input
              type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300}
              placeholder='e.g. "won 3 weeks ago, before tracking started"'
              className="mt-1 w-full border border-slate-200 rounded-md px-2 py-1.5 text-sm"
              data-testid="manual-followup-note-input"
            />
          </div>
          <button
            type="submit" disabled={saving}
            className="w-full text-sm font-semibold bg-teal-600 text-white rounded-md py-2 hover:bg-teal-700 disabled:opacity-50"
            data-testid="manual-followup-submit-btn"
          >
            {saving ? 'Creating…' : 'Create task'}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ManualFollowUpDialog;
